import { guessIfPrinterIsRollo, QzConnectionState, usePrinterContext } from "@/contexts/PrinterContext";
import Button from "@/components/forms/Button/Button";
import { useEffect, useState } from "react";
import Select from "@/components/forms/Select/Select";
import styles from "./page.module.css";
import IconDownloadingFilesFromCloudToLaptop from "@/components/icons/IconDownloadingFilesFromCloudToLaptop";
import IconWindows from "@/components/icons/IconWindows";
import IconDownloadBox from "@/components/icons/IconDownloadBox";
import IconMacOSX from "@/components/icons/IconMacOSX";
import IconLinux from "@/components/icons/IconLinux";
import Checkbox from "@/components/forms/Checkbox/Checkbox";
import { Tooltip } from "@/components/Tooltip/Tooltip";
import { Modal } from "@/components/Modal/Modal";
import ConfirmModal from "@/components/ConfirmModal/ConfirmModal";
import Flex from "@/components/Flex/Flex";
import { LocalStorageKeys } from "@/utils/localStorageUtils";
import SellerAccountBanner from "@/components/SellerAccountBanner/SellerAccountBanner";
import Alert from "@/components/Alert/Alert";
import Switch from "@/components/forms/Switch/Switch";
import PageContainer from "../container/PageContainer";
import IconPrintGoogle from "@/components/icons/IconPrintGoogle";

enum OperatingSystem {
  unknown,
  windows,
  macosx,
  linux,
  android,
  iOS,
}

enum CPUArchitecture {
  unknown,
  x86,
  arm
}

export function BoxemPrintRow() {
  const {
    useBoxemPrint,
    setUseBoxemPrint,
    alwaysDownloadLabels,
    setAlwaysDownloadLabels
  } = usePrinterContext();

  return (
    <Flex align="center" className={styles.switchesRow}>
      <Tooltip placement="right" title="You can use this to disable sending print jobs to your preferred printer(s) temporarily. Instead, they will be downloaded to your browser">
        <Switch
          label="Use Boxem Print"
          labelSide="left"
          value={useBoxemPrint}
          onChange={v => setUseBoxemPrint(v)}
        />
      </Tooltip>

      <p className="strong" style={{margin:"0 10px"}}>&nbsp;·&nbsp;</p>

      <Tooltip 
        title={
          useBoxemPrint
          ? "If a printing job fails, we always try and download the label in your browser. With this enabled, you will download labels in your browser even if the print job was successfully sent to your printer"
          : "If not using Boxem Print, labels will always be downloaded to your browser"
        }
      >
        <Switch
          label="Always Download labels"
          labelSide="left"
          disabled={!useBoxemPrint}
          value={alwaysDownloadLabels}
          onChange={v => setAlwaysDownloadLabels(v)}
        />
      </Tooltip>
    </Flex>
  )
}

export function FNSKUPrinterSettings() {
  const {
    useBoxemPrint,
    connectionState,
    submitJobInProgress,
    printers,
    preferredFNSKULabelPrinter,
    setPreferredFNSKULabelPrinter,
    preferredFNSKULabelSize,
    setPreferredFNSKULabelSize,
    fnskuUse30Up,
    setFnskuUse30Up,
    printTestFNSKULabel,
    printTest30UpSheet,
  } = usePrinterContext();

  const [doneRolloWarning, setDoneRolloWarning] = useState<boolean>(!!window.localStorage.getItem(LocalStorageKeys.rolloWarning));
  const [rolloModalOpen, setRolloModalOpen] = useState<boolean>(false);

  const printerSelectOptions = printers.map(printer => printer.name);

  return (
    <>
      <Flex gap={2}>
        <Select
          fullWidth              
          options={printerSelectOptions}
          disabled={connectionState !== QzConnectionState.connected}
          value={preferredFNSKULabelPrinter?.name}
          placeholder={
            <>
              <IconPrintGoogle />
              Select a preferred printer for printing FNSKU labels
            </>
          }
          onChange={(value: string) => {
            const printer = printers.find((printer) => printer.name === value);
            setPreferredFNSKULabelPrinter(printer);
          }}
          selectionRender={option => {
            return (
              <Flex gap={2} align="center">
                <IconPrintGoogle />
                {option}
              </Flex>
            );
          }}
        />
        {!!preferredFNSKULabelPrinter && (
          <Button
            type="text"
            onClick={() => setPreferredFNSKULabelPrinter(null)}
          >
            Clear
          </Button>
        )}
      </Flex>

      {preferredFNSKULabelPrinter && (
        <Tooltip
          title={
            fnskuUse30Up ? "If you use 30-UP labels, the paper size is implicitly 8.5 x 11 inches, US Letter size. Don't use 30-UPs without a printer that supports this." : ''
          }
        >
          <div className={styles.labelSizeOrientationGrid}>
            <span>Label size:</span>
            <Select
              fullWidth
              disabled={fnskuUse30Up || submitJobInProgress}
              placeholder="Select a preferred label size"
              optionRender={option => {
                return ( 
                  <Flex style={{width:"100%"}} justify="space-between" align="center">
                    <span>{option.name}</span>

                    {option.fakeIn ? 
                      <>
                        <span>{option.fakeIn.width.toLocaleString("en-US", { maximumFractionDigits: 2 })}x{option.fakeIn.height.toLocaleString("en-US", { maximumFractionDigits: 2 })}"</span>
                      </> 
                      : <>
                        <span>{option.in.width.toLocaleString("en-US", { maximumFractionDigits: 2 })}x{option.in.height.toLocaleString("en-US", { maximumFractionDigits: 2 })}"</span>
                      </>
                    }
                  </Flex>
                );
              }}
              options={preferredFNSKULabelPrinter.sizes}
              value={preferredFNSKULabelSize}
              onChange={size => {
                if (guessIfPrinterIsRollo(preferredFNSKULabelPrinter) && !doneRolloWarning) {
                  setRolloModalOpen(true);
                  window.localStorage.setItem(LocalStorageKeys.rolloWarning, LocalStorageKeys.rolloWarning);
                  setDoneRolloWarning(true);
                }
                setPreferredFNSKULabelSize(size);
              }}
            />
            {!!preferredFNSKULabelSize && (
              <Button
                disabled={fnskuUse30Up || submitJobInProgress}
                type="text"
                onClick={() => setPreferredFNSKULabelSize(null)}
              >
                Clear
              </Button>
            )}
            {!preferredFNSKULabelSize && <div></div>}
          </div>
        </Tooltip>
      )}

      <Flex gap={2} justify="start" align="center">
        <Tooltip
          title={"If enabled, we'll submit FNSKU labels to your printer/download them as 30-up sheet(s). These are US letter-sized pieces of paper capable of printing 30 labels per sheet. We discourage this option."}
        >
          <label htmlFor="fnskuUse30Up" className={styles.checkLabel}>
            Use 30-up Labels
            <Checkbox
              id="fnskuUse30Up"
              value={fnskuUse30Up}
              onChange={v => setFnskuUse30Up(v)}
            />
          </label>
        </Tooltip>
      </Flex>

      {!fnskuUse30Up && (
        <>
          <Flex gap={2}>
            <Button
              type="secondary"
              onClick={() => printTestFNSKULabel(true, false)}
            >
              Download test FNSKU label
            </Button>
            <Tooltip title={!useBoxemPrint ? "Enable 'Use Boxem Print' in the top-left first" : !preferredFNSKULabelPrinter ? "Select a printer first" : ""}>
              <Button
                type="secondary"
                disabled={!preferredFNSKULabelPrinter || connectionState !== QzConnectionState.connected || !useBoxemPrint || submitJobInProgress}
                onClick={() => printTestFNSKULabel(false, true)}
              >
                <IconPrintGoogle /> 
                Print test FNSKU label
              </Button>
            </Tooltip>
          </Flex>
        </>
      )}

      {fnskuUse30Up && (
        <>
          <Flex gap={2} align="center">
            <Button
              type="secondary"
              onClick={() => {
                printTest30UpSheet(true, false);
              }}
            >
              Download test 30-up sheet
            </Button>
            
            <Tooltip title={!useBoxemPrint ? "Enable 'Use Boxem Print' in the top-left first" : !preferredFNSKULabelPrinter ? "Select a printer first" : ""}>
              <Button
                type="secondary"
                disabled={!preferredFNSKULabelPrinter || connectionState !== QzConnectionState.connected || !useBoxemPrint || submitJobInProgress}
                onClick={() => {
                  printTest30UpSheet(false, true);
                }}
              >
                <IconPrintGoogle />
                Print test 30-up sheet
              </Button>
            </Tooltip>
          </Flex>
        </>
      )}
    </>
  )
}

export default function SettingsPrintingPage() {

  const [OS, setOS] = useState<OperatingSystem>(OperatingSystem.unknown);
  const [arch, setArch] = useState<CPUArchitecture>(CPUArchitecture.unknown);
  const [isMobile, setIsMobile] = useState<boolean>(false); // mobile = userAgent.indexOf('Mobile') !== -1;

  const [downloadBoxemPrintModalOpen, setDownloadBoxemPrintModalOpen] = useState<boolean>(false);
  const [doneRolloWarning, setDoneRolloWarning] = useState<boolean>(!!window.localStorage.getItem(LocalStorageKeys.rolloWarning));
  const [rolloModalOpen, setRolloModalOpen] = useState<boolean>(false);

  const {
    printers,
    connectionState,
    preferredBoxLabelPrinter,
    setPreferredBoxLabelPrinter,
    preferredBoxLabelSize,
    setPreferredBoxLabelSize,
    printTestBoxLabel,
    alwaysDownloadLabels,
    setAlwaysDownloadLabels,
    useBoxemPrint,
    setUseBoxemPrint,
    submitJobInProgress,
  } = usePrinterContext();

  useEffect(() => {

    // this first case only works on chrome, edge and opera at the moment :(
    //@ts-ignore, typescript doesn't realize this is a valid value on the navigator object (at least for some browsers)
    if (window.navigator.userAgentData) {
      //@ts-ignore
      window.navigator.userAgentData.getHighEntropyValues(["architecture"])
      .then(data => {

        // https://wicg.github.io/ua-client-hints/#user-agent-platform-architecture
        if (data.architecture == "arm") {
          setArch(CPUArchitecture.arm);
        } else {
          setArch(CPUArchitecture.x86);
        }

        // https://wicg.github.io/ua-client-hints/#user-agent-platform-brand
        switch (data?.platform) {
          case "Windows":
            setOS(OperatingSystem.windows);
            break;

          case "macOS":
            setOS(OperatingSystem.macosx);
            break;

          case "Linux": // @TODO i didn't actually verify this is the right string!!!
            setOS(OperatingSystem.linux);
            break;

          default:
            setOS(OperatingSystem.windows);
            return;
        }
      });

    } else {
      // this is probably wrong to do, but I just decided to assume x86 (really, non-arm) windows if we can't figure it out.
      setOS(OperatingSystem.windows); 
      setArch(CPUArchitecture.x86);
      
      if (window.navigator.userAgent.indexOf("Win") != -1)      setOS(OperatingSystem.windows);
      if (window.navigator.userAgent.indexOf("Mac") != -1)      setOS(OperatingSystem.macosx);
      if (window.navigator.userAgent.indexOf("Linux") != -1)    setOS(OperatingSystem.linux);
      // if (window.navigator.userAgent.indexOf("Android") != -1)  setOS(OperatingSystem.android);
      // if (window.navigator.userAgent.indexOf("like Mac") != -1) setOS(OperatingSystem.iOS);
    }
  }, []);

  const printerSelectOptions = printers.map(printer => printer.name);

  let alertType: "info" | "success" | "error" = "info";
  let alertMessage;
  if (connectionState === QzConnectionState.connecting) {
    alertMessage = "Attempting to connect to Boxem Print...";
  } else if (connectionState === QzConnectionState.connected) {
    alertType = "success";
    alertMessage = "Succesfully connected to Boxem Print!";
  } else if (connectionState === QzConnectionState.failed) {
    alertType = "error";
    alertMessage = "Failed to connect to Boxem Print. Install Boxem Print and ensure it is running.";
  } else {
  }

  return (
    <PageContainer>
      <ConfirmModal
        open={rolloModalOpen}
        onClose={confirmed => setRolloModalOpen(false)}
        title={"Rollo Setup"}
        body={
          <p style={{margin:"20px"}}>
          It looks like you're using a Rollo with a custom label size! You may need to configure a custom label size to complete setup. Check out: <a href="https://www.boxem.com/help/rollo-printers" target="_blank">Boxem - Rollo Printers</a>
          </p>
        }
      />

     <Modal
        closeOnOutsideClick={true}
        closeIcon={true}
        open={downloadBoxemPrintModalOpen}
        onOpenChange={(open) => setDownloadBoxemPrintModalOpen(open)}
    >
      <Flex direction="column" className={styles.downloadModalBody}>

        <Flex className={styles.downloadPlatformRow} justify="space-between">
          <Flex direction="column" justify="center" align="center" className={styles.downloadPlatformIconSection}>
            <IconWindows/>
            <h3>Windows</h3>
          </Flex>

          <Flex direction="column" justify="space-around" className={styles.downloadArchColumn}>
            <Flex justify="space-between" align="center" className={styles.downloadArchRow}>
              <Flex direction="column">
                <h3>x86_64</h3>
                <span className="weak">Most PCs</span>
              </Flex>

              <Button
                onClick={() => {}}
              >
                <a
                  className={styles.downloadLink}
                  href="https://public-boxem-binaries.s3.amazonaws.com/boxem-printing-2.2.3-x86_64.exe"
                > 
                  <Flex align="center" gap={2}>
                    <IconDownloadBox/>
                    Download
                  </Flex>
                </a>
              </Button>
            </Flex>

            <Flex justify="space-between" align="center" className={styles.downloadArchRow}>
              <Flex direction="column" grow={1} shrink={0} basis={"0"}>
                <h3>arm64</h3>
                <span className="weak">Windows Dev Kit 2023, etc.</span>
              </Flex>

              <Flex grow={1} shrink={0} basis={"0"}>
                <Button
                  onClick={() => {}}
                >
                  <a
                    className={styles.downloadLink}
                    href="https://public-boxem-binaries.s3.amazonaws.com/boxem-printing-2.2.3-arm64.exe"
                  > 
                    <Flex align="center" gap={2}>
                      <IconDownloadBox/>
                      Download
                    </Flex>
                  </a>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        <Flex className={styles.downloadPlatformRow} justify="space-between">
          <Flex direction="column" justify="center" align="center" className={styles.downloadPlatformIconSection}>
            <IconMacOSX/>
            <h3>MacOSX</h3>
          </Flex>

          <Flex direction="column" justify="space-around" className={styles.downloadArchColumn}>
            <Flex justify="space-between" align="center" className={styles.downloadArchRow}>
              <Flex direction="column">
                <h3>x86_64</h3>
                <span className="weak">Most Macs</span>
              </Flex>

              <Button
                onClick={() => {}}
              >
                <a
                  className={styles.downloadLink}
                  href="https://public-boxem-binaries.s3.amazonaws.com/boxem-printing-2.2.3-x86_64.pkg"
                > 
                  <Flex align="center" gap={2}>
                    <IconDownloadBox/>
                    Download
                  </Flex>
                </a>
              </Button>
            </Flex>

            <Flex justify="space-between" align="center" className={styles.downloadArchRow}>
              <Flex direction="column" grow={1} shrink={0} basis={"0"}>
                <h3>arm64</h3>
                <span className="weak">Apple Silicon/M-series, etc.</span>
              </Flex>

              <Flex grow={1} shrink={0} basis={"0"}>
                <Button
                  onClick={() => {}}
                >
                  <a
                    className={styles.downloadLink}
                    href="https://public-boxem-binaries.s3.amazonaws.com/boxem-printing-2.2.3-arm64.pkg"
                  > 
                    <Flex align="center" gap={2}>
                      <IconDownloadBox/>
                      Download
                    </Flex>
                  </a>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

        <Flex className={styles.downloadPlatformRow} justify="space-between">
          <Flex direction="column" justify="center" align="center" className={styles.downloadPlatformIconSection}>
            <IconLinux/>
            <h3>Linux</h3>
          </Flex>

          <Flex direction="column" justify="space-around" className={styles.downloadArchColumn}>
            <Flex justify="space-between" align="center" className={styles.downloadArchRow}>
              <Flex direction="column">
                <h3>x86_64</h3>
                <span className="weak">Most Thinkpads, etc.</span>
              </Flex>

              <Button
                onClick={() => {}}
              >
                <a
                  className={styles.downloadLink}
                  href="https://public-boxem-binaries.s3.amazonaws.com/boxem-printing-2.2.3-x86_64.run"
                > 
                  <Flex align="center" gap={2}>
                    <IconDownloadBox/>
                    Download
                  </Flex>
                </a>
              </Button>
            </Flex>

            <Flex justify="space-between" align="center" className={styles.downloadArchRow}>
              <Flex direction="column" grow={1} shrink={0} basis={"0"}>
                <h3>arm64</h3>
                <span className="weak">Some Chromebooks, M-series Macbooks, etc.</span>
              </Flex>

              <Flex grow={1} shrink={0} basis={"0"}>
                <Button
                  onClick={() => {}}
                >
                  <a
                    className={styles.downloadLink}
                    href="https://public-boxem-binaries.s3.amazonaws.com/boxem-printing-2.2.3-arm64.run"
                  > 
                    <Flex align="center" gap={2}>
                      <IconDownloadBox/>
                      Download
                    </Flex>
                  </a>
                </Button>
              </Flex>
            </Flex>
          </Flex>
        </Flex>

      </Flex>
    </Modal>

      <div className={styles.body}>
        <SellerAccountBanner />

        <div className={styles.boxemPrintBanner}>
          <Flex align="center">
            <IconDownloadingFilesFromCloudToLaptop/>

            <Flex direction="column">
              <h3>Make shipments easy with Boxem Print</h3>
              <span>Speed up your shipment process by connecting to Boxem Print</span>
            </Flex>
          </Flex>

          <Flex direction="column" gap={2}>
            <Button
              onClick={() => setDownloadBoxemPrintModalOpen(true)}
            >
              Download
            </Button>
          </Flex>
        </div>

        <BoxemPrintRow/>

        {alertMessage && <Alert className={styles.alert} type={alertType} message={alertMessage} />}

        <section className={styles.section}>
          <section className={styles.formGroupLabel}>
            <h3><dfn>FNSKU Labels</dfn></h3>
            <p className="small-text">
              An Amazon-unique term to describe barcodes used to track products and connect them to a seller within a
              fulfillment network.
            </p>
          </section>

          <Flex direction="column" className={styles.formGroup} gap={4}>
            <FNSKUPrinterSettings/>

            <Flex direction="column" gap={2} style={{marginTop:"20px"}}>
              <span className="strong">FNSKU label requirements</span>
              <p className="small-text">
                An FNSKU label should have a height by width dimension from 1&quot; x 2&quot; (2.5 cm x 5.0 cm) up to 2&quot; x 3&quot; (5.1 cm x 7.6 cm). 
                For example, you could use a label that is 1&quot; x 3&quot; (2.5 cm x 7.6 cm) or 2&quot; x 2&quot; (5.1 cm x
                5.1 cm). FNSKU Labels must be black on white, with removable adhesive.
              </p>
            </Flex>
          </Flex>
        </section>

        <section className={styles.section}>
          <section className={styles.formGroupLabel}>
            <h3><dfn>Box Labels</dfn></h3>
            <p className="small-text">
              Box labels refer to the shipping, pallet, and/or carrier labels for an inbound shipment. 
              They should be physically placed on the boxes or pallets being shipped. 
              These labels are used by Amazon, and the carrier (like UPS) respectively for identifying, detailing, and tracking the box.
            </p>
          </section>

          <Flex direction="column" className={styles.formGroup} gap={4}>
            <Flex gap={2}>
              <Select
                fullWidth         
                options={printerSelectOptions}
                disabled={connectionState !== QzConnectionState.connected}
                value={preferredBoxLabelPrinter?.name}
                placeholder={
                  <Flex gap={2}>
                    <IconPrintGoogle />
                    Select a preferred printer for printing Box labels
                  </Flex>
                }
                onChange={(value: string) => {
                  const printer = printers.find((printer) => printer.name === value);
                  setPreferredBoxLabelPrinter(printer);
                }}
                selectionRender={option => {
                  return (
                    <Flex gap={2}>
                      <IconPrintGoogle />
                      {option}
                    </Flex>
                  );
                }}
              />
              {!!preferredBoxLabelPrinter && (
                <Button
                  type="text"
                  onClick={() => setPreferredBoxLabelPrinter(null)}
                >
                  Clear
                </Button>
              )}
            </Flex>

            <Flex gap={2} align="center">
              {preferredBoxLabelPrinter && (
                <>
                  <span style={{minWidth:"max-content"}}>Label size:</span>
                  <Select
                    fullWidth
                    disabled={submitJobInProgress}
                    placeholder="Select a preferred label size"
                    optionRender={option => {
                      return ( 
                        <Flex style={{width:"100%"}} justify="space-between" align="center">
                          <span>{option.name}</span>

                          <span>{option.in.width.toLocaleString("en-US", { maximumFractionDigits: 2 })}x{option.in.height.toLocaleString("en-US", { maximumFractionDigits: 2 })}"</span>
                        </Flex>
                      );
                    }}
                    options={preferredBoxLabelPrinter.sizes}
                    value={preferredBoxLabelSize}
                    onChange={size => setPreferredBoxLabelSize(size)}
                  />
                  {!!preferredBoxLabelSize && (
                    <Button
                      disabled={submitJobInProgress}
                      type="text"
                      onClick={() => setPreferredBoxLabelSize(null)}
                    >
                      Clear
                    </Button>
                  )}
                </>
              )}
            </Flex>

            <Flex justify="end" gap={2}>
              <Button
                type="secondary"
                onClick={() => printTestBoxLabel(true, false)}
              >
                Download test Box label
              </Button>
              <Tooltip title={!useBoxemPrint ? "Enable 'Use Boxem Print' in the top-left first" : !preferredBoxLabelPrinter ? "Select a printer first" : ""}>
                <Button
                  type="secondary"
                  disabled={!preferredBoxLabelPrinter || connectionState !== QzConnectionState.connected || !useBoxemPrint || submitJobInProgress}
                  onClick={() => printTestBoxLabel(false, true)}
                >
                  <Flex gap={2}>
                   <IconPrintGoogle />
                    Print test Box label
                  </Flex>
                </Button>
              </Tooltip>
            </Flex>

            <Flex direction="column" gap={2} style={{marginTop:"20px"}}>
              <span className="strong">Box label requirements</span>
              <p className="small-text">
                Amazon recommends that you use a label with a height by width dimension of 4" x 6" (10.2 cm x 15.2 cm). However you can use a label size up to 6" x 8" (15.2 cm x 20.3 cm). Box labels must be black on white, with permanent adhesive.
              </p>
            </Flex>
          </Flex>
        </section>
      </div>
    </PageContainer>
  );
}